<template>
  <sign-up-modal-shell backdrop="surface" teleport title="Create Your Account">
    <!-- Estado: link de referral inválido -->
    <div v-if="invalid_link" class="signup-modal__result">
      <div class="result-icon result-icon--error">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#DC3545"/></svg>
      </div>
      <h3 class="result__title">Invalid Link</h3>
      <p class="result__text">This invitation link is no longer valid. Please ask for a new one.</p>
    </div>

    <!-- Pantalla de éxito — estilo wizard, dentro del mismo shell -->
    <div v-else-if="showSuccess" class="signup-modal__result signup-modal__result--congrats">
      <div class="result-icon result-icon--success">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="#19A99D"/></svg>
      </div>
      <h3 class="result__title">You're In — Welcome to Kiey!</h3>
      <p class="result__text">
        Your account is now active. Download the Kiey app on a mobile device to get started.
      </p>
      <div class="congrats-stores">
        <a :href="appleStoreUrl" target="_blank" rel="noopener" class="congrats-stores__btn">
          <img :src="require('@/assets/img/apple_store.svg')" alt="Download on the App Store"/>
        </a>
        <a :href="googlePlayUrl" target="_blank" rel="noopener" class="congrats-stores__btn">
          <img :src="require('@/assets/img/google_play.svg')" alt="Get it on Google Play"/>
        </a>
      </div>
    </div>

    <!-- Estado: la dirección no está cubierta por ningún equipo —
         se sumó al usuario a la waitlist. Reusa el lenguaje visual
         de la pantalla de waitlist del wizard. -->
    <div v-else-if="showWaitlist" class="waitlist-v2">
      <p class="waitlist-v2__eyebrow">Waitlist Confirmed</p>
      <div class="waitlist-v2__icon">
        <svg width="34" height="34" viewBox="0 0 24 24" fill="none"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" fill="#2563EB"/></svg>
      </div>
      <h2 class="waitlist-v2__title">You're on the Waitlist</h2>
      <p class="waitlist-v2__body">Kiey isn't available in your area yet, but your spot is saved. We'll email you as soon as local access opens.</p>
      <p v-if="email" class="waitlist-v2__email">Confirmation sent to <a :href="'mailto:' + email">{{ email }}</a></p>
    </div>

    <!-- Estado: la dirección está cubierta por otro equipo —
         se ofrece sumarse a ese equipo. Reusa el estilo
         .team-picker__card para mostrar el equipo. -->
    <div v-else-if="otherTeam" class="signup-modal__result signup-modal__result--picker">
      <h3 class="result__title result__title--compact">Good news — there's a team in your area</h3>
      <p class="result__text result__text--compact">
        This address is served by <strong>{{ otherTeam.company_name }}</strong>.
      </p>

      <p v-if="confirmTeamError" class="team-picker__error">{{ confirmTeamError }}</p>

      <div class="team-picker">
        <div class="team-picker__card" style="cursor: default;">
          <div class="team-picker__card-main">
            <div class="team-picker__name">{{ otherTeam.company_name }}</div>
            <div v-if="otherTeam.city || otherTeam.state" class="team-picker__meta">
              <span v-if="otherTeam.city">{{ otherTeam.city }}<span v-if="otherTeam.state">, {{ otherTeam.state }}</span></span>
              <span v-else>{{ otherTeam.state }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="signup-modal__footer">
        <button
          type="button"
          class="signup-btn signup-btn--text"
          :disabled="confirmingTeam"
          @click="useDifferentAddress"
        >Use a different address</button>
        <div style="flex: 1;"></div>
        <button
          type="button"
          class="signup-btn signup-btn--primary signup-btn--lg"
          :disabled="confirmingTeam"
          @click="confirmJoinTeam"
        >{{ confirmingTeam ? 'Joining…' : 'Join ' + otherTeam.company_name }}</button>
      </div>
    </div>

    <!-- Formulario de referral -->
    <form v-else class="signup-form" novalidate @submit.prevent="onSubmit">
      <!-- Panel de invitación — reemplaza la v-alert verde -->
      <div class="referral-invite">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" class="referral-invite__icon"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="#2F6FED"/></svg>
        <span class="referral-invite__text">
          You've been invited by <strong>{{ nameOwner }}</strong>
        </span>
      </div>

      <div class="signup-form__row">
        <div class="signup-form__field">
          <label for="referral-first-name" class="signup-form__label">First Name</label>
          <input
            id="referral-first-name"
            v-model="first_name"
            type="text"
            class="signup-form__input"
            :class="{ 'signup-form__input--error': formErrors.first_name }"
            autocapitalize="words"
            autocorrect="off"
            spellcheck="false"
            @blur="validateField('first_name')"
            @input="clearFieldError('first_name')"
          />
          <span v-if="formErrors.first_name" class="signup-form__error">{{ formErrors.first_name }}</span>
        </div>
        <div class="signup-form__field">
          <label for="referral-last-name" class="signup-form__label">Last Name</label>
          <input
            id="referral-last-name"
            v-model="last_name"
            type="text"
            class="signup-form__input"
            :class="{ 'signup-form__input--error': formErrors.last_name }"
            autocapitalize="words"
            autocorrect="off"
            spellcheck="false"
            @blur="validateField('last_name')"
            @input="clearFieldError('last_name')"
          />
          <span v-if="formErrors.last_name" class="signup-form__error">{{ formErrors.last_name }}</span>
        </div>
      </div>

      <div class="signup-form__row">
        <div class="signup-form__field">
          <label for="referral-phone" class="signup-form__label">Phone Number</label>
          <input
            id="referral-phone"
            v-model="phone_number"
            v-mask="mask"
            type="tel"
            inputmode="tel"
            autocomplete="tel-national"
            placeholder="xxx - xxx - xxxx"
            class="signup-form__input"
            :class="{ 'signup-form__input--error': formErrors.phone_number }"
            @blur="validateField('phone_number')"
            @input="clearFieldError('phone_number')"
          />
          <span v-if="formErrors.phone_number" class="signup-form__error">{{ formErrors.phone_number }}</span>
        </div>
        <div class="signup-form__field">
          <label for="referral-email" class="signup-form__label">Email</label>
          <input
            id="referral-email"
            v-model="email"
            type="email"
            inputmode="email"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            class="signup-form__input"
            :class="{ 'signup-form__input--error': formErrors.email }"
            @blur="validateField('email')"
            @input="clearFieldError('email')"
          />
          <span v-if="formErrors.email" class="signup-form__error">{{ formErrors.email }}</span>
        </div>
      </div>

      <div class="signup-form__row signup-form__row--full">
        <div class="signup-form__field">
          <label for="referral-address" class="signup-form__label">Address</label>
          <GmapAutocomplete
            id="referral-address"
            placeholder="Start typing your street address…"
            @place_changed="onPlaceChanged"
            :options="{ types: ['address'], componentRestrictions: { country: ['us', 'ca'] } }"
            class="signup-form__input"
            :class="{ 'signup-form__input--error': formErrors.address }"
          ></GmapAutocomplete>
          <span v-if="formErrors.address" class="signup-form__error">{{ formErrors.address }}</span>
          <p v-else-if="locationName" class="signup-form__location-badge">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#19A99D"/></svg>
            {{ locationName }}
          </p>
        </div>
      </div>

      <consent-checkboxes
        :sms-consent.sync="sms_consent"
        :email-consent.sync="email_consent"
      />

      <div class="signup-modal__footer">
        <div style="flex: 1;"></div>
        <button
          type="submit"
          class="signup-btn signup-btn--primary signup-btn--lg"
          :disabled="status === 'loading' || !sms_consent || !email_consent"
        >{{ status === 'loading' ? 'Activating…' : 'Activate' }}</button>
      </div>
    </form>
  </sign-up-modal-shell>
</template>

<script>
import { mask } from "vue-the-mask";
import axios from "axios";
import { mapState } from "vuex";
import config from "@/config/constants";
import SignUpModalShell from "@/views/login/components/SignUpModalShell.vue";
import ConsentCheckboxes from "@/components/forms/ConsentCheckboxes";

export default {
  name: "ClientReferralSignUp",
  directives: { mask },
  components: {
    "sign-up-modal-shell": SignUpModalShell,
    ConsentCheckboxes,
  },
  data: () => ({
    email: "",
    first_name: "",
    last_name: "",
    phone_number: "",
    mask: "### - ### - ####",
    invalid_link: false,
    showSuccess: false,
    // Estado de validación de dirección / mercado. El backend devuelve
    // un `outcome` que ramifica entre registrado / otro equipo / waitlist.
    otherTeam: null,
    showWaitlist: false,
    confirmingTeam: false,
    confirmTeamError: null,
    sms_consent: false,
    email_consent: false,
    // Objeto de ubicación geocodificado por GmapAutocomplete.
    location: null,
    locationName: "",
    formErrors: {
      first_name: null,
      last_name: null,
      phone_number: null,
      email: null,
      address: null,
    },
  }),
  computed: {
    ...mapState({
      status: (state) => state.Client_sign_up.status,
      nameOwner: (state) => state.Client_sign_up.name_owner,
    }),
    appleStoreUrl() {
      return this.$store.getters["Authenticate/getAppleStoreUrl"];
    },
    googlePlayUrl() {
      return this.$store.getters["Authenticate/getGooglePlayUrl"];
    },
  },
  mounted() {
    this.getNameOwner();
    // Registra el "open" del link de marketing. Fire-and-forget: nunca
    // debe afectar el render ni el flujo de sign-up.
    this.trackLinkOpen();
  },
  created() {
    if (this.$store.getters["Authenticate/isLoggedIn"]) {
      this.$store.dispatch("Authenticate/safety_logout");
      this.$store.dispatch("Banner/hide");
    }
  },
  methods: {
    validateField(field) {
      const raw = this[field];
      const value = typeof raw === "string" ? raw.trim() : raw;
      let error = null;

      switch (field) {
        case "first_name":
          if (!value) error = "First name is required";
          break;
        case "last_name":
          if (!value) error = "Last name is required";
          break;
        case "phone_number": {
          if (!value) {
            error = "Phone number is required";
          } else {
            const digits = String(value).replace(/\D/g, "");
            if (digits.length !== 10) error = "Enter a 10-digit phone number";
          }
          break;
        }
        case "email":
          if (!value) {
            error = "Email is required";
          } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            error = "Enter a valid email address";
          }
          break;
        case "address":
          // Misma validación que el wizard: el código postal es la
          // prueba de que es una dirección real (lo setea extractPlaceData).
          if (!this.location) error = "Please pick a complete address from the dropdown.";
          break;
      }

      this.$set(this.formErrors, field, error);
      return !error;
    },

    clearFieldError(field) {
      if (this.formErrors[field]) this.$set(this.formErrors, field, null);
    },

    // Geocodificación de la dirección — mismo enfoque que el wizard:
    // el postal_code es el single-source-of-truth de "es una dirección
    // real" (ciudades y picks a nivel país no lo traen).
    onPlaceChanged(place) {
      if (!place || !place.geometry) {
        this.location = null;
        this.locationName = "";
        this.$set(this.formErrors, "address", "Please pick an address from the dropdown.");
        return;
      }

      const components = {};
      if (place.address_components) {
        for (const comp of place.address_components) {
          for (const t of comp.types) {
            components[t] = { short: comp.short_name, long: comp.long_name };
          }
        }
      }

      if (!components.postal_code) {
        this.location = null;
        this.locationName = "";
        this.$set(
          this.formErrors,
          "address",
          "Please pick a complete street address from the dropdown (with a postal/zip code)."
        );
        return;
      }

      this.$set(this.formErrors, "address", null);
      this.location = {
        address: place.formatted_address || place.name,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        street_number: components.street_number ? components.street_number.long : "",
        postal_code: components.postal_code.long,
        city:
          (components.locality && components.locality.long) ||
          (components.sublocality_level_1 && components.sublocality_level_1.long) ||
          "",
        state:
          (components.administrative_area_level_1 &&
            components.administrative_area_level_1.short) ||
          "",
        // El backend usa `country` para la excepción de Canadá: las
        // direcciones CA se registran con el tenant invitante sin pasar
        // por el matching de mercados.
        country: (components.country && components.country.short) || "",
      };
      this.locationName = place.formatted_address || place.name;
    },

    validateForm() {
      const fields = ["first_name", "last_name", "phone_number", "email", "address"];
      let allValid = true;
      for (const field of fields) {
        if (!this.validateField(field)) allValid = false;
      }
      return allValid;
    },

    // Registra un "open" del link de marketing cuando la página carga.
    // Lee el referral + utm igual que buildParams. Dedupe por localStorage
    // (mk_open_<token>) para no contar recargas. Fire-and-forget: cualquier
    // error se traga para no afectar el render ni el flujo de sign-up.
    trackLinkOpen() {
      try {
        const q = this.$route.query || {};
        const referral = this.$route.params.referral || q.referral;
        if (!referral) return;

        const dedupeKey = "mk_open_" + referral;
        if (window.localStorage && window.localStorage.getItem(dedupeKey)) return;
        if (window.localStorage) window.localStorage.setItem(dedupeKey, "1");

        const urlParams = new URLSearchParams(window.location.search);
        const payload = {
          referral,
          utm_source: q.utm_source || urlParams.get("utm_source") || "",
          utm_medium: q.utm_medium || urlParams.get("utm_medium") || "",
          utm_campaign: q.utm_campaign || urlParams.get("utm_campaign") || "",
          utm_content: q.utm_content || urlParams.get("utm_content") || "",
        };

        axios
          .post(config.api_url + "signup/track-open", payload)
          .catch(() => {});
      } catch (e) {
        // Swallow — el tracking nunca debe romper la página.
      }
    },

    // Arma el cuerpo del POST a add_client. Se extrajo de onSubmit para
    // que tanto el envío inicial como el paso de confirmación de
    // "otro equipo" (re-POST con confirmed_tenant_id) compartan la misma
    // construcción de parámetros.
    buildParams() {
      const self = this;

      let phone = self.phone_number;
      phone = phone.replaceAll(" ", "").replaceAll("-", "");
      phone = config.phone_extension + phone;

      let params = {
        first_name: self.first_name,
        last_name: self.last_name,
        phone_number: phone,
        email: self.email,
        sms_consent: self.sms_consent,
        email_consent: self.email_consent,
        sms_consent_url: window.location.href,
      };
      params["referral"] = self.$route.params.referral
        ? self.$route.params.referral
        : self.$route.query.referral;

      // UTM es para atribución real de campañas — el link de referral
      // ya las trae en la querystring, así que no hace falta pedírselas
      // al homeowner.
      const q = self.$route.query || {};
      const urlParams = new URLSearchParams(window.location.search);
      params["utm_source"] = q.utm_source || urlParams.get("utm_source") || "";
      params["utm_medium"] = q.utm_medium || urlParams.get("utm_medium") || "";
      params["utm_campaign"] = q.utm_campaign || urlParams.get("utm_campaign") || "";
      params["utm_content"] = q.utm_content || urlParams.get("utm_content") || "";

      // Objeto de ubicación geocodificado completo — el backend lo usa
      // para el matching de mercados. Se sigue enviando el string plano
      // `address` por seguridad.
      params["location"] = self.location;
      params["address"] = self.location.address;

      return params;
    },

    onSubmit() {
      const self = this;
      if (!self.validateForm()) return;
      if (!self.sms_consent || !self.email_consent) return;
      if (!self.location) return;

      const params = self.buildParams();

      self.$store
        .dispatch("Client_sign_up/add_client", params)
        .then(function (result) {
          // El backend ramifica según en qué mercado cae la dirección:
          //  - registered → registrado con el tenant invitante (hoy).
          //  - other_team → la dirección la sirve otro equipo; se ofrece
          //    sumarse a ese equipo.
          //  - waitlist   → ningún equipo sirve la dirección.
          const outcome = result && result.outcome;
          if (outcome === "other_team") {
            self.otherTeam = result.team;
          } else if (outcome === "waitlist") {
            self.showWaitlist = true;
          } else {
            self.showSuccess = true;
          }
        })
        .catch(function (error) {
          self.$set(self.formErrors, "phone_number", error);
        });
    },

    // CTA primaria de la pantalla "otro equipo": re-envía add_client con
    // los MISMOS parámetros más confirmed_tenant_id, para registrar al
    // homeowner con el equipo que sirve su dirección.
    confirmJoinTeam() {
      const self = this;
      if (!self.otherTeam || self.confirmingTeam) return;

      self.confirmingTeam = true;
      self.confirmTeamError = null;

      const params = self.buildParams();
      params["confirmed_tenant_id"] = self.otherTeam.tenant_id;

      self.$store
        .dispatch("Client_sign_up/add_client", params)
        .then(function (result) {
          if (result && result.outcome === "registered") {
            self.otherTeam = null;
            self.showSuccess = true;
          } else {
            self.confirmTeamError =
              "We couldn't complete your registration. Please try again.";
          }
          self.confirmingTeam = false;
        })
        .catch(function (error) {
          self.confirmTeamError =
            typeof error === "string"
              ? error
              : "We couldn't complete your registration. Please try again.";
          self.confirmingTeam = false;
        });
    },

    // CTA secundaria de la pantalla "otro equipo": descarta el equipo
    // ofrecido y la dirección, y vuelve al formulario para que el
    // homeowner pruebe otra dirección.
    useDifferentAddress() {
      this.otherTeam = null;
      this.confirmTeamError = null;
      this.location = null;
      this.locationName = "";
    },

    getNameOwner() {
      const self = this;
      let token = self.$route.params.referral
        ? self.$route.params.referral
        : self.$route.query.referral;
      if (token) {
        self.$store
          .dispatch("Client_sign_up/add_name_owner", token)
          .then(function (response) {
            if (response["data"]["code"] && response["data"]["code"] === "ERROR") {
              self.invalid_link = true;
            }
          })
          .catch(function (errors) {
            console.error(errors);
          });
      }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

/* Estilos compartidos de campos / consentimiento / botones / footer /
   result. Los tokens --kiey-* los expone el SignUpModalShell. */
@import "../login/components/signup-form.scss";

/* ── Panel de invitación de referral ────────────────────── */
.referral-invite {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 18px;
  padding: 14px 16px;
  border-radius: var(--radius-card);
  background: var(--kiey-blue-soft);
  border: 1px solid var(--border-soft);
}

.referral-invite__icon {
  flex: 0 0 auto;
  margin-top: 1px;
}

.referral-invite__text {
  font-size: 14px;
  line-height: 1.4;
  color: var(--kiey-navy);
}

.referral-invite__text strong {
  font-weight: 700;
}

@media (max-width: 600px) {
  .referral-invite {
    padding: 12px 14px;
    margin-bottom: 14px;
  }

  .referral-invite__text {
    font-size: 13px;
  }
}
</style>
